/**
 * ClaimGateProvider / useClaimGate - central gate that intercepts product
 * actions for anonymous users and forces them to claim their workspace first.
 *
 * Usage at a call site:
 *   const { requireClaim } = useClaimGate();
 *   requireClaim('connect_own_data', () => doTheThing());
 *
 * For non-anonymous users `requireClaim` invokes the callback immediately. For
 * anonymous users it opens the claim modal and, on successful claim, runs the
 * callback. New gates are added purely via the claimTriggers registry.
 */
import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { isAnonymousUser } from '../../../utils/anonymous';
import ClaimAccountModal from './ClaimAccountModal';
import type { ClaimTrigger } from './claimTriggers';

interface ClaimGateValue {
  requireClaim: (trigger: ClaimTrigger, onProceed: () => void) => void;
}

const ClaimGateContext = createContext<ClaimGateValue>({
  requireClaim: (_trigger, onProceed) => onProceed(),
});

export function useClaimGate(): ClaimGateValue {
  return useContext(ClaimGateContext);
}

export function ClaimGateProvider({ children }: { children: React.ReactNode }) {
  const [trigger, setTrigger] = useState<ClaimTrigger | null>(null);
  const proceedRef = useRef<(() => void) | null>(null);

  const requireClaim = useCallback((t: ClaimTrigger, onProceed: () => void) => {
    if (!isAnonymousUser()) {
      onProceed();
      return;
    }
    proceedRef.current = onProceed;
    setTrigger(t);
  }, []);

  const handleClose = useCallback(() => {
    setTrigger(null);
    proceedRef.current = null;
  }, []);

  const handleClaimed = useCallback(() => {
    const cb = proceedRef.current;
    setTrigger(null);
    proceedRef.current = null;
    cb?.();
  }, []);

  return (
    <ClaimGateContext.Provider value={{ requireClaim }}>
      {children}
      <ClaimAccountModal
        open={trigger !== null}
        trigger={trigger}
        onClose={handleClose}
        onClaimed={handleClaimed}
      />
    </ClaimGateContext.Provider>
  );
}
